import { NgClass } from '@angular/common';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LazyLoadEvent, PrimeTemplate, TableState } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { SplitButton } from 'primeng/splitbutton';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { SERVICE_ENDPOINTS } from '../../../../core/configs/service-endpoints';
import { CustomerDataDetailsDialogComponent } from '../../../../custom/components/admin/customer-data-details-dialog/customer-data-details-dialog.component';
import { CustomerDataEditDialogComponent } from '../../../../custom/components/admin/customer-data-edit-dialog/customer-data-edit-dialog.component';
import { SpsTableComponent } from '../../../../custom/components/sps-table/sps-table.component';
import { TableListProvider } from '../../../../custom/provider/table-list-provider';
import { CustomerStatus } from '../../../enums/organisation/customer-status';
import { OrganisationStatus } from '../../../enums/organisation/organisation-status';
import { OrganisationData } from '../../../interfaces/organisation/organisation-data';
import { CustomerStatusTagPipe } from '../../../pipes/customer-status-tag.pipe';
import { BaseAdminCustomerComponent } from '../../base/base-admin-customer.component';

@Component({
  selector: 'app-admin-customers-view',
  imports: [
    Card,
    PrimeTemplate,
    SpsTableComponent,
    Dialog,
    SplitButton,
    Button,
    TableModule,
    Tooltip,
    CustomerDataDetailsDialogComponent,
    Select,
    Tag,
    CustomerStatusTagPipe,
    FormsModule,
    CustomerDataEditDialogComponent,
    NgClass
  ],
  templateUrl: './admin-customers-view.component.html'
})
export class AdminCustomersViewComponent extends BaseAdminCustomerComponent implements OnInit, AfterViewInit {

  /**
   * Status change dialog
   * only extended params, see base class
   * @protected
   */
  newStatus: CustomerStatus = null;

  constructor() {
    super();
  }

  async ngOnInit() {
    /**
     * Workaround for table refresh error after filter reset
     * route to dummy component and route back
     * and delete the state for the other admin component
     */
    this.redirectRoute = 'admincustomers';
    sessionStorage.removeItem('ADMIN_ONBOARDING_VIEW');

    this.loading = true;
    this.tableListProvider = new TableListProvider(this.i18n);
    this.tableConfig.set(await this.tableConfigService.getTableConfig('SPS_ADMIN_ORGANISATONS_VIEW', false));
    this.rows = this.tableConfig().rows;
    if (this.userSessionSettingsProvider.hasValue('ADMIN_ORGANISATONS_VIEW_ROWS')) {
      this.rows = this.userSessionSettingsProvider.get('ADMIN_ORGANISATONS_VIEW_ROWS');
    }
    /**
     * data from resolver
     */
    this.referralPartnerList = this.route.snapshot.data['refPartnerList'];
    this.tableListProvider.addListByKey('REFERRALPARTNERLIST', this.referralPartnerList);
    this.dataCenterList = this.route.snapshot.data['dataCenterList'];   /**
     * Init menu items for the split-button menu
     */
    this.initAdminMenuItems();
    /**
     * render the html template
     */
    this.loading = false;
    this.tableControlService.refreshTableConfig.set(false);
  }

  ngAfterViewInit(): void {
    /**
     * Some controls need refresh after language-changes
     */
    this.subscriptions.add(
      this.i18n.languageChanged.subscribe({
        next: value => {
          this.refreshControls = true;
          setTimeout(() => {
            this.refreshControls = false;
          }, 10);
        }
      })
    );

    /**
     * Initialize Breadcrumb-Items
     */
    setTimeout(() => {
      this.i18n.breadcrumbMenuItems.set([
        {
          icon: 'pi pi-cog',
          i18nLabelKey: 'MENUCUSTOMERS',
          label: this.i18n.i18nLng()['MENUCUSTOMERS'],
          routerLinkActive: true
        },
      ]);
    }, 50);

  }

  /**
   * Parent-Data-Provider
   * @param lazyLoadEvent
   */
  getOrganisationPageData = (lazyLoadEvent: LazyLoadEvent) => {
    this.lastLazyLoadingEvent = lazyLoadEvent;
    return this.customerDataService.findPage(lazyLoadEvent, SERVICE_ENDPOINTS.GET_ORGANISATIONS);
  };

  /**
   * if row was selected - open details dialog for specific organisation
   * @param organisationData
   */
  onSelectionChanged(organisationData: OrganisationData) {
    this.selectedRowData = null;
    this.selectedRowDataBackup = null;
    this.adminMenuActive = false;
    this.newStatusList = [];
    if (organisationData) {
      this.selectedRowData = organisationData;
      this.selectedRowDataBackup = this.helper.jsonCopy(organisationData);
      this.paymentDataService.selectedOrganisationData = this.selectedRowData;
      this.paymentDataService.selectedOrganisationId = this.selectedRowData.zbvOrganisationId;
      this.paymentDataService.selectedOrganisationName = this.selectedRowData.name;
      this.transactionDataService.selectedOrganisationId = this.selectedRowData.zbvOrganisationId;
      this.transactionDataService.selectedOrganisationName = this.selectedRowData.name;
      /**
       * check if organisatiuon is in onboarding mode
       */
      if (organisationData.organisationStatus !== OrganisationStatus.CUSTOMER) {
        this.adminMenuItems.map(menuItem => menuItem.disabled = true);
      } else {
        this.adminMenuItems.map(menuItem => menuItem.disabled = false);
      }
      this.adminMenuActive = true;
      /**
       * init list for status change (available status)
       */
      this.newStatusList = this.tableListProvider.getListByKey('CUSTOMERSTATUS').filter(item => item.value !== this.selectedRowData.customerStatus && item.value !== CustomerStatus.NEW);
    }
  }

  /**
   * Rows changed by user
   * @param newRowCount
   */
  rowsCountChanged(newRowCount: number) {
    this.rows = newRowCount;
    this.userSessionSettingsProvider.set('ADMIN_ORGANISATONS_VIEW_ROWS', this.rows);
  }

  /**
   * Inititalize the menu-items for the admin views
   * of the customer data
   * @private
   */
  private initAdminMenuItems(): void {
    this.adminMenuItems = [];
    this.adminMenuItems.push(
      {
        id: 'm-customer-data',
        label: this.i18n.i18nLng()['CUSTOMERDATA'],
        icon: 'pi pi-file-edit',
        command: () => {
          this.initOrganisationDataInvalidFlags();
          this.showCustomerDataDialog = true;
        }
      },
      {
        id: 'm-payment-types',
        label: this.i18n.i18nLng()['MENUPAYMENTTYPES'],
        icon: 'pi pi-wallet',
        command: () => {
          this.router.navigate(['/adminpaymenttypes/customer/' + this.selectedRowData.zbvOrganisationId])
        },
      },
      /** Payment Page aktuell ausgeblendet - wird nach go live umgesetzt
      {
        id: 'm-payment-page',
        label: this.i18n.i18nLng()['MENUPAYMENTPAGE'],
        icon: 'pi pi-desktop',
      },
       */
      {
        id: 'm-transactions',
        label: this.i18n.i18nLng()['MENUTRANSACTIONS'],
        icon: 'pi pi-arrow-right-arrow-left',
        command: () => {
          this.router.navigate(['/admintransactions/' + this.selectedRowData.zbvOrganisationId])
        },
      },
      /** Verträge und Preise aktuell ausgeblendet SPSGCH-2153
      {
        id: 'm-contracts',
        label: this.i18n.i18nLng()['CONTRACTS'],
        icon: 'pi pi-file-check',
      },
      {
        id: 'm-pricing',
        label: this.i18n.i18nLng()['PRICING'],
        icon: 'pi pi-euro',
      },
       */
      {
        separator: true
      },
      {
        id: 'm-change-status',
        label: this.i18n.i18nLng()['CUSTOMERSTATUS'],
        icon: 'pi pi-arrow-right-arrow-left',
        command: () => {
          this.showStatusChangeDialog = true;
        }
      }
    );
  }

  /**
   * Table state was restored
   * @protected
   * @param tableState
   */
  protected onStateRestored(tableState: TableState) {
    this.onSelectionChanged(tableState?.selection);
  }

  /**
   * check if orga is in onboarding mode and show info on menu-dropdown
   * @protected
   */
  protected checkOnboardingMode() {
    if (this.selectedRowData.organisationStatus !== OrganisationStatus.CUSTOMER) {
      this.messageService.add({
        life: 3500,
        severity: 'info',
        summary: this.i18n.i18nLng()['ONBOARDINGINPROGRESS'],
        detail: this.i18n.i18nLng()['ONBOARDINGINPROGRESSINFO']
      });
    }
  }

  /**
   * save the changed CustomerStatus
   * @protected
   */
  protected saveNewStatus() {
    this.selectedRowData.customerStatus = this.newStatus;
    this.saveOrganisationData();
    this.newStatus = null;
  }

}
